import { Injectable, inject } from "@angular/core";
import { CanActivate, Router, UrlTree } from "@angular/router";
import { Observable, of } from "rxjs";
import { filter, switchMap, take } from "rxjs/operators";
import { AuthService } from "@auth0/auth0-angular";
import { ToastrService } from "@sdk/lib/toastr/toastr.service";
import { PATIENT_ROLE, THERAPIST_ROLE } from "@sdk/lib/values/roles";
import { LOGIN_PATH } from "@sdk/lib/values/shared-paths";
import { PatientProfile } from "@sdk/lib/types/profile";
import { PatientService } from "@sdk/lib/services/patient.service";
import { StagingRedirectService } from "../shared/services/staging-redirect.service";

@Injectable({
  providedIn: "root",
})
export class HasPatientRoleAccessGuard implements CanActivate {
  private _auth = inject(AuthService);
  private _router = inject(Router);
  private _toastrService = inject(ToastrService);
  private _patientService = inject(PatientService);
  private _stagingRedirect = inject(StagingRedirectService);


  canActivate():
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this._auth.isAuthenticated$.pipe(
      take(1),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          void this._router.navigate(["/", LOGIN_PATH]);
          return [false];
        }
        return this._auth.user$.pipe(
          filter((user) => !!user),
          take(1),
          switchMap((user) =>
            this._patientService.getPatientProfileByUserID(user.sub).pipe(
              switchMap((profile: PatientProfile) => {
                const roleList = profile?.role_list as Array<string>;
                if (
                  roleList &&
                  (roleList.includes(PATIENT_ROLE) || roleList.includes(THERAPIST_ROLE))
                ) {
                  return this._stagingRedirect.keepOnThisEnvironment(profile);
                } else {
                  this._toastrService.error(
                    "You do not have the correct permissions/privileges to access this application. Please contact your organisation's administrator for further information",
                    "Incorrect Privileges/Permissions",
                  );
                  this._auth.logout({ logoutParams: { returnTo: window.location.origin } });
                  return of(false);
                }
              }),
            ),
          ),
        );
      }),
    );
  }
}
